'use client';
import React, { useState, useMemo, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import HeroSection from './components/HeroSection';
import ToolGrid from './components/ToolGrid';
import AdSlot from '@/components/AdSlot';
import MobileBottomNav from './components/MobileBottomNav';
import { TOOL_CATEGORIES, ALL_TOOLS, Tool } from '@/lib/toolRegistry';
import { useTheme } from '@/lib/ThemeContext';
import Link from 'next/link';

// ── ToolCard skeleton ─────────────────────────────────────────────────────────
function ToolCardSkeleton() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <div
      className="rounded-3xl p-4 animate-pulse"
      style={{
        background: isDark ? 'rgba(30,41,59,0.70)' : 'rgba(255,255,255,0.60)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(255,255,255,0.55)',
        boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.25)' : '0 4px 20px rgba(15,23,42,0.06)',
      }}
    >
      <div className={`w-12 h-12 rounded-2xl mb-3 ${isDark ? 'bg-slate-700/80' : 'bg-slate-200/80'}`} />
      <div className={`h-3.5 rounded-full w-3/4 mb-2 ${isDark ? 'bg-slate-700/80' : 'bg-slate-200/80'}`} />
      <div className={`h-3 rounded-full w-full mb-1 ${isDark ? 'bg-slate-700/60' : 'bg-slate-100/80'}`} />
      <div className={`h-3 rounded-full w-2/3 ${isDark ? 'bg-slate-700/60' : 'bg-slate-100/80'}`} />
    </div>
  );
}

// ── Mini tool card for recently used / favorites ──────────────────────────────
function MiniToolCard({
  tool,
  isFav,
  onToggleFav,
  onUse,
}: {
  tool: Tool;
  isFav: boolean;
  onToggleFav: (id: string) => void;
  onUse: (id: string) => void;
}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <Link
      href={tool.route}
      onClick={() => onUse(tool.id)}
      className="group relative flex flex-col gap-2 p-3.5 rounded-2xl transition-all duration-200 hover:-translate-y-1"
      style={{
        background: isDark ? 'rgba(30,41,59,0.75)' : 'rgba(255,255,255,0.72)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: isDark ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(255,255,255,0.60)',
        boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.25)' : '0 4px 16px rgba(15,23,42,0.06)',
      }}
    >
      {/* Fav toggle */}
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggleFav(tool.id);
        }}
        className="absolute top-2.5 right-2.5 w-6 h-6 flex items-center justify-center rounded-full transition-all duration-150 hover:scale-110"
        style={{ background: isFav ? 'rgba(239,68,68,0.12)' : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(148,163,184,0.10)' }}
        aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
      >
        <svg
          className="w-3.5 h-3.5"
          fill={isFav ? '#ef4444' : 'none'}
          viewBox="0 0 24 24"
          stroke={isFav ? '#ef4444' : isDark ? '#64748b' : '#94a3b8'}
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
        </svg>
      </button>

      {/* Icon */}
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
        style={{ background: `${tool.color}${isDark ? '22' : '14'}` }}
      >
        {tool.icon}
      </div>

      {/* Title */}
      <span
        className="text-xs font-semibold leading-tight pr-5 transition-colors"
        style={{ color: isDark ? '#cbd5e1' : '#334155' }}
      >
        {tool.title}
      </span>
    </Link>
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────────
function SectionShell({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <section className="px-4 sm:px-6 pb-8 relative z-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-2.5 mb-4">
          <div
            className="w-8 h-8 rounded-xl flex items-center justify-center"
            style={{ background: isDark ? 'rgba(96,165,250,0.15)' : 'rgba(37,99,235,0.10)' }}
          >
            {icon}
          </div>
          <h2
            className="text-base font-bold"
            style={{ color: isDark ? '#f1f5f9' : '#1e293b' }}
          >
            {title}
          </h2>
        </div>
        {children}
      </div>
    </section>
  );
}

export default function HomePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [isLoading, setIsLoading] = useState(true);
  const [recentIds, setRecentIds] = useState<string[]>([]);
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const categories = ['All', ...TOOL_CATEGORIES];

  // Load localStorage data after mount
  useEffect(() => {
    try {
      const r = localStorage.getItem('mst_recent');
      const f = localStorage.getItem('mst_favorites');
      if (r) setRecentIds(JSON.parse(r));
      if (f) setFavoriteIds(JSON.parse(f));
    } catch {}
    // Simulate brief loading for skeleton effect
    const t = setTimeout(() => setIsLoading(false), 600);
    return () => clearTimeout(t);
  }, []);

  const handleUse = (id: string) => {
    setRecentIds((prev) => {
      const next = [id, ...prev.filter((x) => x !== id)].slice(0, 8);
      try { localStorage.setItem('mst_recent', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const handleToggleFav = (id: string) => {
    setFavoriteIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [id, ...prev];
      try { localStorage.setItem('mst_favorites', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const recentTools = recentIds.map((id) => ALL_TOOLS.find((t) => t.id === id)).filter(Boolean) as Tool[];
  const favoriteTools = favoriteIds.map((id) => ALL_TOOLS.find((t) => t.id === id)).filter(Boolean) as Tool[];

  const filteredTools = useMemo(() => {
    const q = searchQuery?.toLowerCase()?.trim();
    return ALL_TOOLS?.filter((tool) => {
      const matchesSearch =
        !q ||
        tool?.title?.toLowerCase()?.includes(q) ||
        tool?.description?.toLowerCase()?.includes(q) ||
        tool?.keywords?.some((k) => k?.toLowerCase()?.includes(q));
      const matchesCategory =
        activeCategory === 'All' || tool?.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, activeCategory]);

  const isFiltering = searchQuery?.trim() !== '' || activeCategory !== 'All';

  return (
    <main className="min-h-screen relative pb-20 md:pb-0">
      {/* Floating gradient blobs background */}
      <div className="bg-blobs" aria-hidden="true">
        <div className="bg-blob bg-blob-1 blob-float-1" />
        <div className="bg-blob bg-blob-2 blob-float-2" />
        <div className="bg-blob bg-blob-3 blob-float-3" />
        <div className="bg-blob bg-blob-4 blob-float-4" />
      </div>

      <div className="relative z-10">
        <Header />
        <HeroSection />

        {/* ── Search + Filter Bar ── */}
        <section id="search" className="px-4 sm:px-6 pb-6 -mt-2 relative z-20">
          <div className="max-w-3xl mx-auto">
            {/* Search input */}
            <div className="relative mb-4">
              <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                <svg
                  className="w-5 h-5"
                  style={{ color: isDark ? '#475569' : '#94a3b8' }}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                </svg>
              </div>
              <input
                type="search"
                placeholder="Search tools — PDF, image, audio, QR..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-12 pr-5 py-4 rounded-2xl text-sm font-medium outline-none transition-all duration-200 focus:ring-2 focus:ring-blue-400/40"
                style={{
                  background: isDark ? 'rgba(30,41,59,0.80)' : 'rgba(255,255,255,0.80)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: isDark ? '1.5px solid rgba(255,255,255,0.10)' : '1.5px solid rgba(255,255,255,0.65)',
                  boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.25)' : '0 4px 24px rgba(37,99,235,0.08), 0 1px 4px rgba(0,0,0,0.05)',
                  color: isDark ? '#e2e8f0' : '#334155',
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-4 flex items-center transition-colors"
                  style={{ color: isDark ? '#475569' : '#94a3b8' }}
                  aria-label="Clear search"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Category filter chips */}
            <div className="flex flex-wrap gap-2">
              {categories?.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className="px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 hover:scale-105"
                  style={
                    activeCategory === cat
                      ? {
                          background: 'linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%)',
                          color: '#ffffff',
                          boxShadow: '0 4px 14px rgba(37,99,235,0.35)',
                        }
                      : {
                          background: isDark ? 'rgba(30,41,59,0.80)' : 'rgba(255,255,255,0.72)',
                          backdropFilter: 'blur(12px)',
                          WebkitBackdropFilter: 'blur(12px)',
                          border: isDark ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(255,255,255,0.6)',
                          boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.20)' : '0 2px 8px rgba(0,0,0,0.05)',
                          color: isDark ? '#94a3b8' : '#475569',
                        }
                  }
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Ad slot below hero */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 pb-6">
          <AdSlot type="horizontal" label="Advertisement" />
        </div>

        {/* ── Loading Skeletons ── */}
        {isLoading ? (
          <section className="py-6 px-4 sm:px-6 relative z-10">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {Array.from({ length: 10 }).map((_, i) => (
                  <ToolCardSkeleton key={i} />
                ))}
              </div>
            </div>
          </section>
        ) : isFiltering ? (
          /* ── Filtered results ── */
          <section id="tools" className="py-6 px-4 sm:px-6 relative z-10">
            <div className="max-w-7xl mx-auto">
              {filteredTools.length === 0 ? (
                /* ── Empty search state ── */
                <div
                  className="text-center py-20 rounded-3xl"
                  style={{
                    background: isDark ? 'rgba(30,41,59,0.70)' : 'rgba(255,255,255,0.65)',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(255,255,255,0.5)',
                  }}
                >
                  <div className="text-6xl mb-4">🔍</div>
                  <h3
                    className="text-lg font-bold mb-2"
                    style={{ color: isDark ? '#f1f5f9' : '#334155' }}
                  >
                    No tools found
                  </h3>
                  <p
                    className="text-sm mb-6"
                    style={{ color: isDark ? '#64748b' : '#64748b' }}
                  >
                    No results for &ldquo;{searchQuery}&rdquo;. Try a different keyword or browse by category.
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {['PDF', 'Image', 'Audio', 'QR', 'Text'].map((s) => (
                      <button
                        key={s}
                        onClick={() => setSearchQuery(s)}
                        className="px-4 py-2 rounded-full text-xs font-semibold transition-all hover:scale-105"
                        style={{
                          background: isDark ? 'rgba(96,165,250,0.12)' : 'rgba(37,99,235,0.08)',
                          border: isDark ? '1px solid rgba(96,165,250,0.25)' : '1px solid rgba(37,99,235,0.18)',
                          color: isDark ? '#60a5fa' : '#2563eb',
                        }}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => { setSearchQuery(''); setActiveCategory('All'); }}
                    className="mt-4 text-sm font-medium transition-colors underline underline-offset-2"
                    style={{ color: isDark ? '#475569' : '#64748b' }}
                  >
                    Clear filters
                  </button>
                </div>
              ) : (
                <>
                  <p
                    className="text-sm font-medium mb-5"
                    style={{ color: isDark ? '#64748b' : '#64748b' }}
                  >
                    {filteredTools.length} tool{filteredTools.length !== 1 ? 's' : ''} found
                    {searchQuery ? ` for "${searchQuery}"` : ''}
                    {activeCategory && activeCategory !== 'All' ? ` in ${activeCategory}` : ''}
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {filteredTools.map((tool) => (
                      <MiniToolCard
                        key={tool.id}
                        tool={tool}
                        isFav={favoriteIds.includes(tool.id)}
                        onToggleFav={handleToggleFav}
                        onUse={handleUse}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </section>
        ) : (
          /* ── Default view: special sections + full grid ── */
          <>
            {/* Recently Used */}
            {recentTools.length > 0 && (
              <SectionShell
                title="Recently Used"
                icon={
                  <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
              >
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2.5">
                  {recentTools.map((tool) => (
                    <MiniToolCard
                      key={tool.id}
                      tool={tool}
                      isFav={favoriteIds.includes(tool.id)}
                      onToggleFav={handleToggleFav}
                      onUse={handleUse}
                    />
                  ))}
                </div>
              </SectionShell>
            )}

            {/* Favorites */}
            {favoriteTools.length > 0 && (
              <SectionShell
                title="Favorites"
                icon={
                  <svg className="w-4 h-4 text-red-400" fill="#ef4444" viewBox="0 0 24 24" stroke="#ef4444" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                  </svg>
                }
              >
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2.5">
                  {favoriteTools.map((tool) => (
                    <MiniToolCard
                      key={tool.id}
                      tool={tool}
                      isFav={true}
                      onToggleFav={handleToggleFav}
                      onUse={handleUse}
                    />
                  ))}
                </div>
              </SectionShell>
            )}

            {/* Full Tool Grid */}
            <ToolGrid />
          </>
        )}

        <Footer />
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
        onSearchFocus={() => {
          document.getElementById('search')?.scrollIntoView({ behavior: 'smooth' });
        }}
      />
    </main>
  );
}